import type { ProColumns } from '@ant-design/pro-components';
import { ProTable } from '@ant-design/pro-components';
import { Avatar, Space, Tag } from 'antd';
import { useState } from 'react';
import { ExamRankItemDTO, examAttemptController } from '../../api';
import { useCompany } from '../../hooks/company';
import AttemptAdminResultDrawer from './AttemptAdminResultDrawer';
import { formatDuration } from './constants';

const medalColor: Record<number, string> = { 1: '#f5b041', 2: '#aab7b8', 3: '#cd6155' };

// 排行榜（考试成绩页的 Tab）：每人取最高分成绩，全员可见；
// 有考试管理或部门成绩权限的人可以点开答题详情
export default ({ examId }: { examId: I.ID }) => {
  const { id: companyId, currentUser, hasPermission } = useCompany();
  const [detailAttemptId, setDetailAttemptId] = useState<I.ID>();

  const canViewDetail =
    hasPermission('page:exam:manage') ||
    hasPermission('page:exam:score:department');

  const columns: ProColumns<ExamRankItemDTO>[] = [
    {
      title: '名次',
      dataIndex: 'rank',
      width: 80,
      render: (_, record) => {
        const rank = record.rank || 0;
        if (rank <= 3) {
          return (
            <b style={{ color: medalColor[rank], fontSize: 16 }}>No.{rank}</b>
          );
        }
        return rank;
      },
    },
    {
      title: '姓名',
      dataIndex: 'userName',
      render: (_, record) => (
        <Space>
          <Avatar src={record.avatarUrl || undefined} size="small">
            {(record.userName || '?').slice(0, 1)}
          </Avatar>
          <span
            style={
              record.userId === currentUser?.id ? { fontWeight: 600 } : undefined
            }
          >
            {record.userName}
          </span>
          {record.userId === currentUser?.id && <Tag>我</Tag>}
        </Space>
      ),
    },
    { title: '部门', dataIndex: 'departmentName' },
    { title: '得分', dataIndex: 'score', width: 90 },
    {
      title: '是否通过',
      dataIndex: 'pass',
      width: 100,
      render: (_, record) =>
        record.pass ? <Tag color="green">通过</Tag> : <Tag color="red">未通过</Tag>,
    },
    {
      title: '用时',
      dataIndex: 'durationSeconds',
      width: 110,
      render: (_, record) => formatDuration(record.durationSeconds),
    },
    { title: '交卷时间', dataIndex: 'submittedAt', width: 170 },
  ];

  if (canViewDetail) {
    columns.push({
      title: '操作',
      valueType: 'option',
      width: 100,
      render: (_, record) => (
        <a onClick={() => setDetailAttemptId(record.attemptId as I.ID)}>
          答题详情
        </a>
      ),
    });
  }

  return (
    <>
      <ProTable<ExamRankItemDTO>
        rowKey="userId"
        columns={columns}
        search={false}
        options={false}
        params={{ examId }}
        pagination={{ pageSize: 20 }}
        request={async ({ current, pageSize }) => {
          const data = await examAttemptController.rank(
            companyId,
            examId,
            current,
            pageSize,
          );
          return { data: data.list, success: true, total: data.total };
        }}
        onRow={(record) =>
          record.userId === currentUser?.id
            ? { style: { background: '#e6f4ff' } }
            : {}
        }
      />
      {detailAttemptId && (
        <AttemptAdminResultDrawer
          attemptId={detailAttemptId}
          onClose={() => setDetailAttemptId(undefined)}
        />
      )}
    </>
  );
};
